import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Result,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { AdminUser, UserRole, UserStatus } from './types';
import { useUsers } from './queries';
import { UserFormModal } from './UserFormModal';
import { formatDateTime } from '../../utils/format';
import { UserActions } from './UserActions';
import { OAuthSourceBadges } from './OAuthSourceBadges';

const ROLE_COLORS: Record<UserRole, string> = {
  user: 'blue',
  admin: 'gold',
};

export const UsersPage: React.FC<{ fixedRole?: UserRole }> = ({ fixedRole }) => {
  const navigate = useNavigate();
  const detailPath = (id: string) => `${fixedRole === 'admin' ? '/administrators' : '/users'}/${encodeURIComponent(id)}`;
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>();
  const [statusFilter, setStatusFilter] = useState<UserStatus | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const { data, error, isLoading, isPlaceholderData, refetch } = useUsers(
    page,
    search,
    fixedRole ?? roleFilter,
    statusFilter
  );
  const openCreateModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user: AdminUser) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load users"
        subTitle={
          error instanceof Error ? error.message : 'An unexpected error occurred'
        }
        extra={
          <Button type="primary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const columns: ColumnsType<AdminUser> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90, render: (value: string) => <Link to={detailPath(value)}>{value}</Link> },
    { title: 'Username', dataIndex: 'username', key: 'username', render: (value: string, user) => <Link to={detailPath(user.id)}>{value}</Link> },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (value: string | null, user) => value ? <Link to={detailPath(user.id)}>{value}</Link> : '—',
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'OAuth source',
      dataIndex: 'oauth_sources',
      key: 'oauth_sources',
      render: (sources: AdminUser['oauth_sources']) => <OAuthSourceBadges sources={sources} />,
    },
    { title: 'VIP level', dataIndex: 'vip_level', key: 'vip_level', width: 100, render: (value: number) => value > 0 ? <Tag color="gold">VIP {value}</Tag> : '—' },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 90,
      render: (value: UserRole) => (
        <Tag color={ROLE_COLORS[value]}>{value.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: UserStatus) => (
        <Tag color={value === 'active' ? 'green' : 'red'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: formatDateTime,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: formatDateTime,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, user) => <UserActions user={user} onDetails={() => navigate(detailPath(user.id))} onEdit={() => openEditModal(user)} />,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <Space wrap>
          <Input
            placeholder="Search ID, username, name, or email"
            prefix={<SearchOutlined className="text-slate-400" />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onPressEnter={() => {
              setSearch(searchInput);
              setPage(1);
            }}
            allowClear
            style={{ width: 300 }}
          />
          {!fixedRole && <Select
            aria-label="Role filter"
            className="min-w-32"
            allowClear
            placeholder="All roles"
            value={roleFilter}
            onChange={(value) => {
              setRoleFilter(value);
              setPage(1);
            }}
            options={[
              { value: 'user', label: 'User' },
              { value: 'admin', label: 'Admin' },
            ]}
          />}
          <Select
            aria-label="Status filter"
            className="min-w-32"
            allowClear
            placeholder="All statuses"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
          <Button
            onClick={() => {
              setSearch(searchInput);
              setPage(1);
            }}
          >
            Search
          </Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          {fixedRole === 'admin' ? 'Create administrator' : 'Create user'}
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading}
        scroll={{ x: 1100 }}
        pagination={{
          current: data?.meta.page ?? page,
          pageSize: 10,
          total: data?.meta.total ?? 0,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          onChange: setPage,
          disabled: isPlaceholderData,
        }}
      />
      <UserFormModal
        open={modalOpen}
        user={editingUser}
        defaultRole={fixedRole}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};
